import React, { useEffect, useState } from 'react';
import WhatsAppInbox from './WhatsAppInbox';
import WhatsAppInsights from './WhatsAppInsights';
import WhatsAppSupervisor from './WhatsAppSupervisor';
import WhatsAppCampaigns from './WhatsAppCampaigns';
import WhatsAppTools from './WhatsAppTools';
import { useToast } from './Toast';
import { simulateConversation, clearConversations, getWhatsAppIntegrations } from '../utils/api';
import { FaWhatsapp, FaChartLine, FaPlay, FaTrash, FaUsers, FaBullhorn, FaTools } from 'react-icons/fa';
import './WhatsApp.css';

function calculateDaysUntilExpiry(expiresAt) {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
}

/** Worst-case token state across all integrations (from include_expiration list). */
function summarizeIntegrationTokenAlert(integrations) {
  if (!Array.isArray(integrations) || integrations.length === 0) return null;
  let hasExpired = false;
  let hasUrgent = false;
  for (const row of integrations) {
    const te = row?.token_expiration;
    if (!te?.expiresAt) continue;
    const days = calculateDaysUntilExpiry(te.expiresAt);
    const invalid = te.valid === false;
    if (invalid || (days != null && days < 0)) {
      hasExpired = true;
      break;
    }
    if (days != null && days <= 1) hasUrgent = true;
  }
  if (hasExpired) return 'expired';
  if (hasUrgent) return 'urgent';
  return null;
}

const WhatsApp = ({ language = 'ar', userRole = 'employee', userPermissions = null, currentUserId = null, conversationToOpen = null, onConversationOpened, onOpenWhatsAppIntegrationSettings }) => {
  const toast = useToast();
  const [activeSection, setActiveSection] = useState('inbox'); // 'inbox' | 'campaigns' | 'tools' | 'insights' | 'supervisor' | 'settings'

  // Permission helper — null means full access (owner/manager)
  const can = (key) => {
    if (userRole === 'owner' || userRole === 'manager') return true;
    if (!userPermissions) return true;
    return !!userPermissions[key];
  };
  /** Queue control: reassign, auto-assign, bulk read/dismiss, etc. — owner/manager always; employees need wa_inbox_triage (delegated). */
  const canInboxTriage = can('wa_inbox_triage');
  const CAMPAIGNS_SUBTAB_STORAGE_KEY = 'wa_campaigns_subtab_session_v1';
  const TOOLS_SUBTAB_STORAGE_KEY = 'wa_tools_subtab_session_v1';
  const [campaignsInitialSubTab, setCampaignsInitialSubTab] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem(CAMPAIGNS_SUBTAB_STORAGE_KEY);
      return saved === 'templates' ? 'templates' : 'campaigns';
    } catch (_) {
      return 'campaigns';
    }
  }); // 'campaigns' | 'templates'
  const [toolsInitialSubTab, setToolsInitialSubTab] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem(TOOLS_SUBTAB_STORAGE_KEY);
      return saved === 'interactive' ? 'interactive' : 'quickreplies';
    } catch (_) {
      return 'quickreplies';
    }
  }); // 'quickreplies' | 'interactive'
  const [campaignReportFocusId, setCampaignReportFocusId] = useState(null);
  const [conversationIdToOpen, setConversationIdToOpen] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tokenAlert, setTokenAlert] = useState(null);
  const isDevMode = process.env.NODE_ENV === 'development';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await getWhatsAppIntegrations(true);
        if (cancelled) return;
        setTokenAlert(summarizeIntegrationTokenAlert(data?.integrations));
      } catch {
        if (!cancelled) setTokenAlert(null);
      }
    };
    load();
    const iv = setInterval(load, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    try { window.sessionStorage.setItem(CAMPAIGNS_SUBTAB_STORAGE_KEY, campaignsInitialSubTab); } catch (_) {}
  }, [campaignsInitialSubTab]);

  useEffect(() => {
    try { window.sessionStorage.setItem(TOOLS_SUBTAB_STORAGE_KEY, toolsInitialSubTab); } catch (_) {}
  }, [toolsInitialSubTab]);

  // When App passes a conversation to open (from Clients page or quick-convo button),
  // snap to the inbox section so the user lands in the right place automatically.
  useEffect(() => {
    if (conversationToOpen) {
      setActiveSection('inbox');
    }
  }, [conversationToOpen]);

  // Role-based access control (kept for backward compat; prefer can() for new checks)
  const isManagerOrOwner = userRole === 'manager' || userRole === 'owner';

  const handleSimulate = async () => {
    if (!isDevMode) return;
    
    console.log('🎭 WhatsApp: Starting simulation...');
    setSimulating(true);
    try {
      const response = await simulateConversation();
      console.log('✅ WhatsApp: Simulation completed:', response.data);
      
      toast.success(language === 'ar' 
        ? `تم محاكاة المحادثة بنجاح! الهاتف: ${response.data.phone}`
        : `Conversation simulated successfully! Phone: ${response.data.phone}`
      );
      
      // Trigger refetch of conversations list from server
      // Server ordering (priority_score DESC, last_activity_at DESC) is source of truth
      setRefreshKey(prev => {
        const newKey = prev + 1;
        console.log(`🔄 WhatsApp: Incrementing refreshKey from ${prev} to ${newKey}`);
        return newKey;
      });
    } catch (error) {
      console.error('❌ WhatsApp: Simulation error:', error);
      toast.error(language === 'ar' 
        ? 'فشل محاكاة المحادثة'
        : 'Failed to simulate conversation'
      );
    } finally {
      setSimulating(false);
    }
  };

  const handleClearConversations = async () => {
    if (!isDevMode) return;
    
    const confirmMessage = language === 'ar' 
      ? 'هل أنت متأكد من حذف جميع المحادثات؟ هذا الإجراء لا يمكن التراجع عنه.'
      : 'Are you sure you want to delete all conversations? This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setClearing(true);
    try {
      const response = await clearConversations();
      console.log('✅ WhatsApp: Conversations cleared:', response.data);
      
      toast.success(language === 'ar' 
        ? `تم حذف ${response.data.deleted_count} محادثة بنجاح`
        : `Successfully deleted ${response.data.deleted_count} conversations`
      );
      
      // Trigger refetch to show empty state
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ WhatsApp: Error clearing conversations:', error);
      toast.error(language === 'ar' 
        ? 'فشل حذف المحادثات'
        : 'Failed to clear conversations'
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="wa-animate-in wa-page-root">
      {/* Development Simulation Banner */}
      {isDevMode && (
        <div className="wa-dev-banner">
          <div>
            <strong>{language === 'ar' ? 'وضع التطوير' : 'Development Mode'}</strong>
            <div className="wa-dev-desc">
              {language === 'ar' 
                ? 'محاكاة محادثة واتساب للاختبار'
                : 'Simulate WhatsApp conversation for testing'}
            </div>
          </div>
          <div className="wa-dev-buttons">
            <button
              onClick={handleClearConversations}
              disabled={clearing}
              className="wa-dev-btn wa-dev-btn-danger"
            >
              <FaTrash />
              {clearing 
                ? (language === 'ar' ? 'جاري الحذف...' : 'Clearing...')
                : (language === 'ar' ? 'حذف الكل' : 'Clear All')
              }
            </button>
            <button
              onClick={handleSimulate}
              disabled={simulating}
              className="wa-dev-btn wa-dev-btn-primary"
            >
              <FaPlay />
              {simulating 
                ? (language === 'ar' ? 'جاري المحاكاة...' : 'Simulating...')
                : (language === 'ar' ? 'تشغيل المحاكاة' : 'Run Simulation')
              }
            </button>
          </div>
        </div>
      )}

      {tokenAlert === 'expired' && (
        <div className="wa-token-banner wa-token-banner--expired" role="alert">
          <div className="wa-token-banner__text">
            <strong>{language === 'ar' ? 'انتهت صلاحية توكن واتساب' : 'WhatsApp token expired'}</strong>
            <span>
              {language === 'ar'
                ? 'قد لا تُرسَل الرسائل أو تُستقبل بشكل صحيح حتى يجدّد المسؤول التوكن في الإعدادات ← واتساب.'
                : 'Messages may not send or sync reliably until an admin renews the token under Settings → WhatsApp.'}
            </span>
          </div>
          {(userRole === 'owner' || userRole === 'manager') && typeof onOpenWhatsAppIntegrationSettings === 'function' && (
            <button type="button" className="wa-token-banner__btn" onClick={onOpenWhatsAppIntegrationSettings}>
              {language === 'ar' ? 'إعدادات واتساب' : 'WhatsApp settings'}
            </button>
          )}
          {!(userRole === 'owner' || userRole === 'manager') && (
            <span className="wa-token-banner__hint">
              {language === 'ar'
                ? 'اطلب من مسؤول مؤسستك تحديث تكامل واتساب.'
                : 'Ask your organization admin to update the WhatsApp integration.'}
            </span>
          )}
        </div>
      )}
      {tokenAlert === 'urgent' && (
        <div className="wa-token-banner wa-token-banner--urgent" role="status">
          <div className="wa-token-banner__text">
            <strong>{language === 'ar' ? 'توكن واتساب على وشك الانتهاء' : 'WhatsApp token expiring soon'}</strong>
            <span>
              {language === 'ar'
                ? 'جدّد التوكن اليوم أو غداً لتجنب تعطيل الإرسال والاستقبال.'
                : 'Renew the token today or tomorrow to avoid delivery issues.'}
            </span>
          </div>
          {(userRole === 'owner' || userRole === 'manager') && typeof onOpenWhatsAppIntegrationSettings === 'function' && (
            <button type="button" className="wa-token-banner__btn wa-token-banner__btn--secondary" onClick={onOpenWhatsAppIntegrationSettings}>
              {language === 'ar' ? 'إعدادات واتساب' : 'WhatsApp settings'}
            </button>
          )}
        </div>
      )}

      <div
        className="wa-tabs"
        role="tablist"
        aria-label={language === 'ar' ? 'أقسام واتساب' : 'WhatsApp sections'}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === 'inbox'}
          id="wa-tab-inbox"
          onClick={() => setActiveSection('inbox')}
          className={`wa-tab ${activeSection === 'inbox' ? 'active' : ''}`}
        >
          <FaWhatsapp aria-hidden />
          {language === 'ar' ? 'المحادثات' : 'Conversations'}
        </button>
        {(can('wa_campaigns_view') || can('wa_campaigns_create') || can('wa_templates_manage')) && (
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === 'campaigns'}
            id="wa-tab-campaigns"
            onClick={() => setActiveSection('campaigns')}
            className={`wa-tab ${activeSection === 'campaigns' ? 'active' : ''}`}
          >
            <FaBullhorn aria-hidden />
            {language === 'ar' ? 'الحملات' : 'Campaigns'}
          </button>
        )}
        {(can('wa_tools_use') || can('wa_tools_manage')) && (
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === 'tools'}
            id="wa-tab-tools"
            onClick={() => setActiveSection('tools')}
            className={`wa-tab ${activeSection === 'tools' ? 'active' : ''}`}
          >
            <FaTools aria-hidden />
            {language === 'ar' ? 'الأدوات' : 'Tools'}
          </button>
        )}
        {can('wa_insights') && (
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === 'insights'}
          id="wa-tab-insights"
          onClick={() => setActiveSection('insights')}
          className={`wa-tab ${activeSection === 'insights' ? 'active' : ''}`}
        >
          <FaChartLine aria-hidden />
          {language === 'ar' ? 'التحليلات' : 'Insights'}
        </button>
        )}
        {can('wa_supervisor') && (
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === 'supervisor'}
            id="wa-tab-supervisor"
            onClick={() => setActiveSection('supervisor')}
            className={`wa-tab ${activeSection === 'supervisor' ? 'active' : ''}`}
          >
            <FaUsers aria-hidden />
            {language === 'ar' ? 'الإشراف' : 'Supervisor'}
          </button>
        )}
      </div>

      <div
        className={`wa-page-section${activeSection === 'inbox' ? ' wa-page-section--inbox' : ' wa-page-section--scroll'}`}
      >
        {activeSection === 'inbox' ? (
          <WhatsAppInbox
            refreshKey={refreshKey}
            language={language}
            userRole={userRole}
            canInboxTriage={canInboxTriage}
            currentUserId={currentUserId}
            conversationIdToOpen={conversationIdToOpen}
            onConversationOpened={() => setConversationIdToOpen(null)}
            conversationToOpen={conversationToOpen}
            onConversationToOpenHandled={onConversationOpened}
            onNavigateToTemplates={can('wa_templates_manage') ? () => { setCampaignsInitialSubTab('templates'); setActiveSection('campaigns'); } : undefined}
            onNavigateToTools={(can('wa_tools_use') || can('wa_tools_manage')) ? () => setActiveSection('tools') : undefined}
          />
        ) : activeSection === 'supervisor' ? (
          <WhatsAppSupervisor language={language} onOpenConversation={(id) => { setConversationIdToOpen(id); setActiveSection('inbox'); }} />
        ) : activeSection === 'campaigns' ? (
          <WhatsAppCampaigns
            language={language}
            userRole={userRole}
            initialSubTab={campaignsInitialSubTab}
            onTabChange={(tab) => setCampaignsInitialSubTab(tab)}
            allowCampaignsSubTab={can('wa_campaigns_view') || can('wa_campaigns_create')}
            allowTemplatesSubTab={can('wa_templates_manage')}
            onOpenCampaignReport={(campaignId) => {
              setCampaignReportFocusId(campaignId);
              setActiveSection('insights');
            }}
          />
        ) : activeSection === 'tools' ? (
          <WhatsAppTools
            language={language}
            userRole={userRole}
            userPermissions={userPermissions}
            initialSubTab={toolsInitialSubTab}
            onTabChange={(tab) => setToolsInitialSubTab(tab)}
          />
        ) : (
          <WhatsAppInsights
            language={language}
            userRole={userRole}
            focusedCampaignId={campaignReportFocusId}
            onNavigateToInbox={() => setActiveSection('inbox')}
            onNavigateToSupervisor={() => setActiveSection('supervisor')}
            onNavigateToCampaigns={() => setActiveSection('campaigns')}
            onOpenConversation={(id) => { setConversationIdToOpen(id); setActiveSection('inbox'); }}
          />
        )}
      </div>
    </div>
  );
};

export default WhatsApp;
